import { createContext, ReactNode, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  confirmText?: string;
};

type Request = ConfirmOptions & { resolve: (ok: boolean) => void };
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm must be used within ConfirmProvider");
  return confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const confirm = useCallback<ConfirmFn>((options) => new Promise((resolve) => {
    setRequest({ ...options, resolve });
  }), []);
  const finish = (ok: boolean) => {
    request?.resolve(ok);
    setRequest(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmView request={request} onFinish={finish} />
    </ConfirmContext.Provider>
  );
}

function ConfirmView({ request, onFinish }: { request: Request | null; onFinish: (ok: boolean) => void }) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [typed, setTyped] = useState("");
  const needed = request?.confirmText ?? "";
  const blocked = needed.length > 0 && typed !== needed;
  return (
    <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) onFinish(false); }}>
      <AlertDialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelRef.current?.focus();
        }}
        onCloseAutoFocus={() => setTyped("")}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{request?.title}</AlertDialogTitle>
          {request?.description ? <AlertDialogDescription>{request.description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        {needed ? (
          <label className="space-y-1 text-sm">
            <span>{t("kit.typeToConfirm", { text: needed })}</span>
            <Input value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" className="min-h-11 text-base" />
          </label>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef}>{request?.cancelLabel ?? t("kit.cancel")}</AlertDialogCancel>
          <Button
            type="button"
            variant={request?.destructive ? "destructive" : "default"}
            disabled={blocked}
            onClick={() => onFinish(true)}
          >
            {request?.confirmLabel ?? (request?.destructive ? t("kit.delete") : t("kit.confirm"))}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
